<template>
  <div class="settings-page">
    <Card>
      <template #content>
        <TabView id="settingsTabs">
          <!-- My Account Tab -->
          <TabPanel :header="$t('settings_panel.myAccount')">
            <Card>
              <template #title>{{ $t('settings_panel.myAccount') }}</template>
              <template #content>
                <div class="my-account">
                  <div class="profile-card">
                    <div class="profile-header">
                      <img :src="$utils.getAvatar(my_profile.avatar)" :alt="$t('settings_panel.profilePicture')">
                      <Button :label="$t('settings_panel.editUserProfile')" severity="help" @click="openProfileDialog"
                        id="editProfileButton" size="small" />
                    </div>
                    <div class="profile-content">
                      <h2>@{{ my_profile.nickname }}</h2>
                      <div class="profile-section">
                        <div class="info">
                          <label>{{ $t('settings_panel.displayName') }}</label>
                          <span>{{ my_profile.nickname }}</span>
                        </div>
                        <div class="info">
                          <label>{{ $t('settings_panel.email') }}</label>
                          <span>{{ maskEmail(my_profile.email) }}</span>
                          <button>{{ $t('settings_panel.change') }}</button>
                        </div>
                        <div class="info">
                          <label>{{ $t('settings_panel.language') }}</label>
                          <Dropdown :options="languages" optionLabel="label" optionValue="code"
                            v-model="selectedLanguage" @change="changeLanguage" placeholder="Select Language" />
                        </div>
                        <div class="info">
                          <label>{{ $t('settings_panel.theme') }}</label>
                          <span></span>
                          <Button :icon="icon" severity="secondary" @click="toggleColorScheme()" outlined
                            class="theme-icon" />
                        </div>
                        <div class="info">
                          <label>{{ $t('settings_panel.premium') }}</label>
                          <span>{{ $t('settings_panel.off') }}</span>
                          <button @click="managePremium">{{ $t('settings_panel.managePremium') }}</button>
                        </div>
                        <div class="info">
                          <label>{{ $t('settings_panel.tokens') }}</label>
                          <span>{{ my_profile.golds }}</span>
                          <button @click="loadCredits">{{ $t('settings_panel.loadTokens') }}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </Card>
          </TabPanel>

          <!-- Privacy & Safety Tab -->
          <TabPanel :header="$t('settings_panel.privacySafety')">
            <Card>
              <template #title>{{ $t('settings_panel.privacySafety') }}</template>
              <template #content>
                <div class="privacy-safety">
                  <h3>{{ $t('settings_panel.privacySettings') }}</h3>
                  <div class="setting-item d-none-important">
                    <Checkbox v-model="localSettings" value="clearMessages" />
                    <label>{{ $t('settings_panel.clearPrivateMessages') }}</label>
                  </div>
                  <div class="setting-item">
                    <Checkbox v-model="localSettings" value="showSpam" />
                    <label>{{ $t('settings_panel.doNotShowSpamMessages') }}</label>
                  </div>
                  <div class="setting-item">
                    <Checkbox v-model="localSettings" value="showNSFW" />
                    <label>{{ $t('settings_panel.showNSFWMessagesWithoutBlur') }}</label>
                  </div>
                  <div class="setting-item">
                    <Checkbox v-model="localSettings" value="showBlockedUsersMessage" />
                    <label>{{ $t('settings_panel.showBlockedUsersMessageInChannel') }}</label>
                  </div>
                </div>
              </template>
            </Card>
          </TabPanel>

          <!-- Notifications Tab -->
          <TabPanel :header="$t('settings_panel.notifications')">
            <Card>
              <template #title>{{ $t('settings_panel.notifications') }}</template>
              <template #content>
                <div class="notifications">

                  <h3>{{ $t('settings_panel.displaySettings') }}</h3>
                  <div class="setting-item">
                    <Checkbox v-model="localSettings" value="timestamp" />
                    <label>{{ $t('settings_panel.timestamp') }}</label>
                  </div>
                  <div class="setting-item">
                    <Checkbox v-model="localSettings" value="typing" />
                    <label>{{ $t('settings_panel.typing') }}</label>
                  </div>
                  <div class="setting-item d-none-important">
                    <Checkbox v-model="localSettings" value="reactions" />
                    <label>{{ $t('settings_panel.reactions') }}</label>
                  </div>

                  <h3>{{ $t('settings_panel.notificationSettings') }}</h3>
                  <div class="setting-item">
                    <Checkbox v-model="localSettings" value="sound" />
                    <label>{{ $t('settings_panel.soundAlertNewMessage') }}</label>
                  </div>
                  <div class="setting-item">
                    <Checkbox v-model="localSettings" value="email" />
                    <label>{{ $t('settings_panel.emailNotifications') }}</label>
                  </div>
                  <div class="setting-item d-none-important">
                    <Checkbox v-model="localSettings" value="push" />
                    <label>{{ $t('settings_panel.pushNotifications') }}</label>
                  </div>
                </div>
              </template>
            </Card>
          </TabPanel>

          <!-- Security Tab -->
          <TabPanel :header="$t('settings_panel.security')">
            <!-- Change Password Form -->
            <Card>
              <template #title>{{ $t('settings_panel.changePassword') }}</template>
              <template #content>
                <form>
                  <div class="change-password-form">
                    <div class="field">
                      <label for="currentPassword">{{ $t('settings_panel.currentPassword') }}</label>
                      <Password id="currentPassword" v-model="passwords.current" autocomplete="current-password" />
                    </div>
                    <div class="field">
                      <label for="newPassword">{{ $t('settings_panel.newPassword') }}</label>
                      <Password id="newPassword" v-model="passwords.new" autocomplete="new-password" />
                    </div>
                    <div class="field">
                      <label for="confirmPassword">{{ $t('settings_panel.confirmNewPassword') }}</label>
                      <Password id="confirmPassword" v-model="passwords.confirm" autocomplete="new-password" />
                    </div>
                    <Button :label="$t('settings_panel.changePassword')" @click="changePassword" size="small" />
                  </div>
                </form>
              </template>
            </Card>


            <Card>
              <template #title>JWT Key For Bot</template>
              <template #content>
                <div class="delete-account">
                  <p class="warning">
                   Key: {{ securityDetails.auth }}
                  </p>
                  <p class="warning">
                   User ID: {{ securityDetails.user_id }}
                  </p>
                </div>
              </template>
            </Card>
            

            <!-- Account Deletion Warning -->
            <Card>
              <template #title>{{ $t('settings_panel.deleteAccount') }}</template>
              <template #content>
                <div class="delete-account">
                  <p class="warning">
                    {{ $t('settings_panel.deleteAccountWarning') }}
                  </p>
                  <Button :label="$t('settings_panel.deleteAccount')" class="p-button-danger"
                    @click="confirmDeleteAccount" size="small" />
                </div>
              </template>
            </Card>
          </TabPanel>

          <!-- Blocked List Tab -->
          <TabPanel :header="$t('settings_panel.blockedList')">
            <Card>
              <template #title>{{ $t('settings_panel.blockedList') }}</template>
              <template #content>
                <div class="blocked-list">
                  <h3>{{ $t('settings_panel.blockedUsers') }}</h3>
                  <div v-if="blocks && blocks.length">
                    <DataTable :value="blocks">
                      <Column field="nickname" :header="$t('settings_panel.username')"></Column>
                      <Column :header="$t('settings_panel.actions')">
                        <template #body="slotProps">
                          <Button :label="$t('settings_panel.unblock')" icon="pi pi-user-plus"
                            @click="unblockUser(slotProps.data)" size="small" />
                        </template>
                      </Column>
                    </DataTable>
                  </div>
                  <div v-else>
                    <p>{{ $t('settings_panel.noBlockedUsers') }}</p>
                  </div>
                </div>
              </template>
            </Card>
          </TabPanel>

          <!-- Billing Tab -->
          <TabPanel :header="$t('settings_panel.billing')">
            <Card>
              <template #title>{{ $t('settings_panel.billing') }}</template>
              <template #content>
                <div class="billing">
                  <h3>{{ $t('settings_panel.paymentHistory') }}</h3>
                  <div v-if="paymentHistory && paymentHistory.length">
                    <DataTable :value="paymentHistory">
                      <Column :header="$t('settings_panel.amount')">
                        <template #body="slotProps">
                          {{ slotProps.data.amount }} {{ slotProps.data.currency }}
                        </template>
                      </Column>
                      <Column field="product_name" :header="$t('settings_panel.status')"></Column>

                      <Column field="created_at" :header="$t('settings_panel.date')"></Column>

                    </DataTable>
                  </div>
                  <div v-else>
                    <p>{{ $t('settings_panel.noPaymentHistory') }}</p>
                  </div>
                </div>
              </template>
            </Card>
          </TabPanel>

          <!-- Token History Tab -->
          <TabPanel :header="$t('settings_panel.tokenHistory')">
            <Card>
              <template #title>{{ $t('settings_panel.tokenHistory') }}</template>
              <template #content>
                <div class="token-history">
                  <h3>{{ $t('settings_panel.tokenUsageHistory') }}</h3>
                  <div v-if="tokenHistory && tokenHistory.length">
                    <DataTable :value="tokenHistory">
                      <Column field="nickname" :header="$t('settings_panel.targetUsername')">
                        <template #body="slotProps">
                          <span @click="goToProfile(slotProps.data)" class="cursor-pointer">
                            {{ slotProps.data.nickname }}
                          </span>
                        </template>
                      </Column>
                      <Column field="used_gold" :header="$t('settings_panel.tokensUsed')"></Column>
                      <Column field="action" :header="$t('settings_panel.action')">
                        <template #body="slotProps">
                          <span v-html="getTokenAction(slotProps.data.action)"></span>
                        </template>
                      </Column>
                      <Column field="created_at" :header="$t('settings_panel.date')"></Column>
                    </DataTable>
                  </div>
                  <div v-else>
                    <p>{{ $t('settings_panel.noTokenHistory') }}</p>
                  </div>
                </div>
              </template>
            </Card>
          </TabPanel>

          <!-- Withdraw Tab -->
          <TabPanel :header="$t('settings_panel.withdraw')">
            <Card>
              <template #title>{{ $t('settings_panel.withdraw') }}</template>
              <template #content>
                <div class="withdraw">
                  <h3>{{ $t('settings_panel.withdrawTokens') }}</h3>
                  <div v-if="my_profile.status == 3 || uploadedDocuments">
                    <div class="withdrawal-form">
                      <p>{{ $t('settings_panel.withdrawTokensInfo', {
                        tokens: my_profile.golds, amount:
                          (my_profile.golds * my_profile.gold_rate) + ' ' +g_settings.currency
                      }) }}</p>

                      <p>{{ $t('g_settings.withdrawal_instructions') }}</p>
                      <div class="field">
                        <label for="amountToWithdraw">{{ $t('settings_panel.amountToWithdraw') }}</label>
                        <InputNumber id="amountToWithdraw" v-model="amountToWithdraw"
                          :placeholder="'Max: ' + my_profile.golds" />
                      </div>
                      <div class="field">
                        <label for="withdrawAddress">{{ $t('settings_panel.withdrawAddress') }}</label>
                        <InputText id="withdrawAddress" v-model="withdrawAddress"
                          :placeholder="$t('settings_panel.withdrawAddress')" />
                      </div>
                      <div class="field">
                        <label for="withdrawMemo">{{ $t('settings_panel.withdrawMemo') }}</label>
                        <Textarea v-model="withdrawMemo" rows="5" cols="30"
                          :placeholder="$t('settings_panel.withdrawMemo')" />
                      </div>
                      <Button :label="$t('settings_panel.submitWithdrawal')" @click="submitWithdrawal" />

                      <div class="mt-2">
                        <h3 class="mb-2">{{ $t('settings_panel.transactionHistory') }}</h3>
                        <DataTable :value="withdrawHistories" class="p-datatable-sm">
                          <Column  :header="$t('settings_panel.table.amount')">
                            <template #body="slotProps">
                              {{ slotProps.data.amount }} {{ g_settings.currency_symbol }}
                            </template>
                          </Column>
                          <Column field="used_gold" :header="$t('settings_panel.table.used')"></Column>
                          <Column field="address" :header="$t('settings_panel.table.address')"></Column>
                          <Column field="memo" :header="$t('settings_panel.table.memo')"></Column>
                          <Column field="created_at" :header="$t('settings_panel.table.date')"></Column>
                          <Column :header="$t('settings_panel.table.status')">
                            <template #body="slotProps">
                              <span>
                                {{ statusTemplate(slotProps.data) }}
                              </span>
                            </template>
                          </Column>
                        </DataTable>
                      </div>


                    </div>
                  </div>
                  <div v-else>
                    <p v-if="my_profile.status == 2" class="mb-2 mt-2">{{ $t('settings_panel.verifyIdentityMessage2') }}
                    </p>
                    <p>{{ $t('settings_panel.verifyIdentityMessage') }}</p>

                    <FileUpload name="files[]" :url="$socket_url + '/uploadDocuments'" @before-send="onBeforeUpload"
                      :multiple="true" accept="image/*" :maxFileSize="1000000" @error="handleError"
                      @finished="handleFinished">
                      <template #empty>
                        <span>{{ $t('settings_panel.drag_drop') }}</span>
                      </template>
                    </FileUpload>

                  </div>
                </div>
              </template>
            </Card>
          </TabPanel>

          <!-- Help Requests Tab -->
          <TabPanel :header="$t('settings_panel.helpRequests')">
            <Card>
              <template #title>{{ $t('settings_panel.ticketList') }}</template>
              <template #content>
                <div v-if="tickets && tickets.length">
                  <Accordion value="0">
                    <AccordionPanel v-for="ticket in tickets" :key="ticket.id.toString()" :value="ticket.id.toString()">
                      <AccordionHeader @click="goToTicket(ticket.id)" class="ticket-header">
                        <div>
                          <div class="ticket-subject">{{ getSubject(ticket.subject) }} </div>
                          <div class="ticket-status" v-html="getTicketStatus(ticket.status)"></div>
                        </div>
                        {{ ticket.created_at }}
                      </AccordionHeader>
                      <AccordionContent>
                        <ul class="ticket-messages">
                          <li v-for="message in ticket.messages" :key="message.id" class="ticket-message">
                            <p class="ticket-message-content">{{ message.message }}</p>
                            <p class="ticket-message-meta"><span>{{ message.nickname }}</span> <span>{{
                              message.created_at }} </span></p>
                          </li>
                        </ul>

                        <Textarea v-model="ticket.message" rows="5" cols="30" class="p-fluid" :required="true" />

                        <Button :label="$t('settings_panel.reply')" icon="pi pi-send" class="p-button-success ml-auto"
                          size="small" @click="replyToTicket(ticket.id, ticket.message)" />
                      </AccordionContent>
                    </AccordionPanel>
                  </Accordion>
                </div>
                <div v-else>
                  <p>{{ $t('settings_panel.noTickets') }}</p>
                </div>
              </template>
            </Card>
          </TabPanel>
        </TabView>
      </template>
    </Card>
  </div>
  <Toast />
</template>

<script>
export default {
  name: 'SettingsDialog',
  props: ['my_profile', 'settings', 'blocks', 'g_settings','securityDetails'],
  emits: ['update-profile', 'send-events'],
  data() {
    return {
      icon: 'pi pi-sun',
      localSettings: [],
      paymentHistory: [],
      tokenHistory: [],
      tickets: [],
      withdrawHistories: [],
      amountToWithdraw: 0,
      passwords: {
        current: '',
        new: '',
        confirm: '',
      },
      withdrawAddress: '',
      withdrawMemo: '',
      parentUpdate: true,
      languages: [
        { label: 'English', code: 'en' },
        { label: 'Türkçe', code: 'tr' },
        { label: 'Español', code: 'es' },
        // Diğer diller
      ],
      selectedLanguage: this.$i18n.locale,
      uploadedDocuments: false
    };
  },
  watch: {
    localSettings: {
      handler(newSettings) {
        if (this.parentUpdate == false) {
          this.$emit('send-events', { action: 'settings', settings: newSettings });
        }
      },
      deep: true,
    },
    blocks: {
      handler(newBlocks) {
        console.log(newBlocks);
      },
      deep: true,
    },
  },
  mounted() {





    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      this.selectedLanguage = savedLanguage;
      this.$i18n.locale = savedLanguage;
    }
    this.parentUpdate = true;
    this.localSettings = this.settings;
    setTimeout(() => {
      this.parentUpdate = false;
    }, 200);

    this.$socket.on('tokenHistory', (data) => {
      this.tokenHistory = data;
    });
    this.$socket.on('paymentHistory', (data) => {
      this.paymentHistory = data;
    });
    this.$socket.on('ticketHistory', (data) => {
      this.tickets = [];
      data.forEach((ticket) => {
        this.tickets.push({ ...ticket, messages: [], message: '' });
      });
    });

    this.$socket.on('getTicketDetails', (data) => {
      let ticket = this.tickets.find((ticket) => ticket.id == data.ticket_id);
      if (ticket) {
        ticket.messages = data.messages;
      }
    });
    this.$socket.on('replyToTicketResult', (data) => {
      if (data.status) {
        let ticket = this.tickets.find((ticket) => ticket.id == data.ticket_id);
        if (ticket) {
          ticket.messages.push(data);
        }
      }
    });

    this.$socket.on('withdrawHistories', (data) => {
      this.withdrawHistories = data;
    })

    this.$socket.on('withdrawSuccess', () => {
      this.$toast.add({
        severity: 'success',
        summary: this.$t('error_messages.info'),
        detail: this.$t('settings_panel.withdrawSuccess'),
        life: 3000,
      })
    })


    this.$socket.on('change-password-result', (data) => {
      if (data.status && data.status == 'ok') {
        this.$toast.add({
          severity: 'success',
          summary: this.$t('settings_panel.success'),
          detail: this.$t('settings_panel.passwordChanged'),
          life: 3000,
        });
      } else {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('settings_panel.error'),
          detail: this.$t('settings_panel.passwordChangeFailed'),
          life: 3000,
        });
      }
    });
    this.$socket.emit('getMyList');
    this.$socket.emit('withdrawHistory');

    console.log(this.localSettings);

  },
  unmounted() {
    this.$socket.off('tokenHistory');
    this.$socket.off('paymentHistory');
    this.$socket.off('ticketHistory');
    this.$socket.off('getTicketDetails');
    this.$socket.off('replyToTicketResult');
    this.$socket.off('change-password-result');
    this.$socket.off('withdrawHistories');
    this.$socket.off('withdrawSuccess');
  },
  methods: {
    onBeforeUpload(event) {
      console.log(event);
      const xhr = event.xhr;
      xhr.setRequestHeader('Authorization', this.$socket.authKeyUnique.auth);
      xhr.setRequestHeader('User_ID', this.$socket.authKeyUnique.user_id);
    },
    changeLanguage() {
      this.$i18n.locale = this.selectedLanguage;
      // İsteğe bağlı: Kullanıcı tercihlerini yerel depolamada saklama
      localStorage.setItem('preferredLanguage', this.selectedLanguage);
    },
    replyToTicket(ticketId, message) {
      this.$socket.emit('replyToTicket', {
        ticket_id: ticketId,
        message: message,
      });
    },
    statusTemplate(rowData) {
      let statusText = '';
      switch (rowData.status) {
        case 0:
          statusText = 'Waiting';
          break;
        case 1:
          statusText = 'Completed';
          break;
        case 2:
          statusText = 'Rejected';
          break;
      }
      return statusText;
    },
    getTokenAction(action) {
      switch (action) {
        case 1:
          return `<span class="p-badge p-component p-badge-warn">${this.$t('settings_panel.sent')}</span>`;
        case 2:
          return `<span class="p-badge p-component p-badge-success">${this.$t('settings_panel.received')}</span>`;
        default:
          return `<span class="p-badge p-component p-badge-info">${this.$t('settings_panel.loaded')}</span>`;
      }
    },
    getSubject(subject) {
      switch (subject) {
        case 'technical':
          return this.$t('supportTicket.topics.technical');
        case 'billing':
          return this.$t('supportTicket.topics.billing');
        case 'general':
          return this.$t('supportTicket.topics.general');
        case 'aboutAccount':
          return this.$t('supportTicket.topics.aboutAccount');
        default:
          return this.$t('supportTicket.topics.other');
      }
    },
    maskEmail(email) {
      const [username, domain] = email.split('@');
      const maskedUsername = `${username[0]}${'*'.repeat(username.length - 2)}${username.slice(-1)}`;
      return `${maskedUsername}@${domain}`;
    },
    toggleColorScheme() {

      this.updateIcon();
    },
    updateIcon() {
      const root = document.getElementsByTagName('html')[0];
      if (root.classList.contains('p-dark')) {
        this.$emit('update-theme', 'dark');
        this.icon = 'pi pi-moon';
      } else {
        this.$emit('update-theme', 'light');
        this.icon = 'pi pi-sun';
      }
    },
    managePremium() {
      this.$emit('send-events', { action: 'open-diamond-dialog' });
    },
    openProfileDialog() {
      this.$emit('send-events', { action: 'open-profile' });
    },
    changePassword() {
      if (this.passwords.current.length < 2) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('settings_panel.rejected'),
          detail: this.$t('settings_panel.currentPasswordTooShort'),
          life: 3000,
        });
        return;
      }
      if (this.passwords.new.length < 5) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('settings_panel.rejected'),
          detail: this.$t('settings_panel.passwordTooShort'),
          life: 3000,
        });
        return;
      }
      if (this.passwords.new !== this.passwords.confirm) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('settings_panel.rejected'),
          detail: this.$t('settings_panel.passwordsDoNotMatch'),
          life: 3000,
        });
        return;
      }
      this.$socket.emit('change-password', {
        current: this.passwords.current,
        password: this.passwords.new,
      });
    },
    getTicketStatus(status) {
      switch (status) {
        case 0:
          return `<span class="p-badge p-component p-badge-warn">${this.$t('settings_panel.closed')}</span>`;
        case 1:
          return `<span class="p-badge p-component p-badge-success">${this.$t('settings_panel.open')}</span>`;
        default:
          return `<span class="p-badge p-component p-badge-info">${this.$t('settings_panel.responded')}</span>`;
      }
    },
    goToTicket(id) {
      this.$socket.emit('showTicketDetails', id);
    },
    confirmDeleteAccount() {
      this.$confirm.require({
        message: this.$t('settings_panel.deleteAccountConfirmation'),
        header: this.$t('settings_panel.confirmation'),
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
          label: this.$t('settings_panel.cancel'),
          severity: 'secondary',
          outlined: true,
        },
        acceptProps: {
          label: this.$t('settings_panel.yesDeleteMyAccount'),
        },
        accept: () => {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('settings_panel.rejected'),
            detail: this.$t('settings_panel.accountDeletionRejected'),
            life: 3000,
          });
        },
        reject: () => { },
      });
    },
    goToProfile(data) {
      this.$emit('send-events', {
        action: 'open-user-profile',
        user: { user_id: data.target_id, nickname: data.nickname, avatar: data.avatar },
      });
    },
    unblockUser(user) {
      this.$emit('send-events', { action: 'unblock', user_id: user.target_id });
    },
    submitWithdrawal() {
      if (this.withdrawAddress === '' || this.withdrawMemo === '' || !this.amountToWithdraw) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('settings_panel.error'),
          detail: this.$t('settings_panel.fillTheForm'),
          life: 3000,
        });
        return;
      }

      if (this.amountToWithdraw < 100) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('settings_panel.error'),
          detail: alert(this.$t('settings_panel.minTokensForWithdraw', { amount: 100 })),
          life: 3000,
        });
        return;
      }

      this.$socket.emit('withdraw', {
        amount: this.amountToWithdraw,
        address: this.withdrawAddress,
        memo: this.withdrawMemo,
      });
    },
    verifyIdentity() {
      // Logic to start identity verification
      console.log('Starting identity verification.');
    },
    loadCredits() {
      this.$emit('send-events', { action: 'open-credit-dialog' });
    },
    handleError() {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('settings_panel.error'),
        detail: this.$t('settings_panel.fileUploadError'),
        life: 3000,
      });
    },
    handleFinished() {
      this.uploadedDocuments = true;
      this.$toast.add({
        severity: 'success',
        summary: this.$t('settings_panel.success'),
        detail: this.$t('settings_panel.fileUploadSuccess'),
        life: 3000,
      });
    },
  },
};
</script>

<style>
.settings-page {
  padding: 20px;
  width: 100%;
}

.profile-card {
  background-color: var(--p-neutral-50);
  max-width: 600px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.profile-header {
  background-color: #36476b;
  height: 100px;
  position: relative;
}

.profile-header img {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  position: absolute;
  bottom: -40px;
  left: 20px;
  border: 4px solid #ffffff;
}

.profile-content {
  padding: 60px 20px 20px 20px;
}

.profile-content h2 {
  margin: 0;
  font-size: 20px;
  color: var(--p-neutral-700);
}

.profile-content p {
  color: var(--p-neutral-600);
  font-size: 14px;
  margin: 5px 0;
}

.profile-section {
  margin-top: 20px;
}

.p-fluid {
  width: 100%;
}

.profile-section label {
  font-weight: bold;
  color: var(--p-neutral-600);
  display: block;
  margin: 0px;
  width: 150px;
}

.profile-section .info {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--p-neutral-400);
}

.profile-section .info:last-child {
  border-bottom: none;
}


.profile-section .info button:not(.theme-icon) {
  padding: 5px 10px;
  font-size: 14px;
  background-color: #404040;
  color: #ffffff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-left: auto;
}

.theme-icon {
  background-color: #404040 !important;
  margin-left: auto;
}

.theme-icon span {
  color: #ffffff !important;

}

.profile-section .info button:hover {
  background-color: #737373;
}

.setting-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.setting-item label {
  margin-left: 10px;
}

button#editProfileButton {
  position: absolute;
  top: 80px;
  right: 10px;
}

.change-password-form label {
  display: block;
  width: 100%;
}

.blocked-list,
.billing,
.token-history,
.withdraw,
.change-password-form,
.delete-account,
.privacy-safety {
  margin-top: 20px;
}

.warning {
  color: red;
}

/* Responsive Design */
@media (min-width: 800px) {
  #settingsTabs .p-tabview-tablist-container {
    float: left;
    width: 200px;
  }

  #settingsTabs .p-tabview-tablist {
    display: flex;
    flex-direction: column;
  }

  #settingsTabs .p-tabview-ink-bar {
    display: none;
  }

  #settingsTabs .p-tabview-tablist-item-active a {
    border-color: var(--p-tabview-tab-active-border-color);
  }

  #settingsTabs .p-tabview-panels {
    float: left;
    width: calc(100% - 220px);
    margin-left: 20px;
  }

}

@media screen and (max-width: 768px) {
  .settings-page {
    padding: 0;
  }

  .p-card-body {
    padding: 0 !important;
  }
}

.cursor-pointer {
  cursor: pointer;
}

li.ticket-message {
  background: var(--p-neutral-200);
  padding: 10px;
  border-radius: 10px;
  margin-bottom: 5px;
}


ul.ticket-messages {
  list-style: none;
  padding: 0;
}

p.ticket-message-meta {
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: 12px;
  font-weight: bold
}

.d-none-important {
  display: none !important;
}

.withdraw .field {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
}
</style>
